import { useState, useRef, useEffect } from 'react';
import { Box, ActionIcon, Transition } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useLocation } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import AskAIButton from './AskAIButton';
import KayakSearchButton from './KayakSearchButton';
import { coral } from 'theme/colors';
import { shadows } from 'theme/shadows';
import { motion } from 'theme/motion';

const FloatingActionMenu = ({ onOpenAIDialog, onOpenKayakDialog }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 56.25em)');
  const { pathname } = useLocation();
  const closeTimer = useRef(null);

  // The FAB is fixed at a high z-index, so it would otherwise float on top of any
  // open dialog (e.g. the AI Flight Deals modal). Hide it while a Mantine Modal or
  // Drawer is *open* — covers every dialog on these routes, not just FAB-triggered ones.
  // Detect via `aria-modal="true"`: Mantine keeps a Modal's root element in the DOM
  // even when closed, but the content (the only node carrying aria-modal) mounts only
  // while opened. It lives inside a portal, so the observer must watch the subtree.
  const [overlayOpen, setOverlayOpen] = useState(false);
  useEffect(() => {
    const syncOverlay = () => setOverlayOpen(document.querySelector('[aria-modal="true"]') !== null);
    syncOverlay();
    const observer = new MutationObserver(syncOverlay);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const routesToShow = [
    '/my-apps',
    '/points',
    '/flight-deal-scanner',
    '/itinerary-ai',
    '/itinerary-ai-plan',
    '/itinerary-ai-plan-editor',
    '/preview/ai-trip'
  ];

  if (!routesToShow.includes(pathname)) return null;
  if (overlayOpen) return null;

  const handleMouseEnter = () => {
    if (!isMobile) {
      clearTimeout(closeTimer.current);
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      closeTimer.current = setTimeout(() => setIsOpen(false), 300);
    }
  };

  const handleFabClick = () => {
    if (isMobile) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleAction = (action) => () => {
    action();
    setIsOpen(false);
  };

  return (
    <Box
      style={{
        position: 'fixed',
        bottom: '5%',
        right: isMobile ? '5%' : '2%',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'flex-end',
        gap: 12
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main FAB -- stays in DOM to preserve layout, visually hides via CSS */}
      <ActionIcon
        onClick={handleFabClick}
        variant="filled"
        size={56}
        radius="xl"
        style={{
          backgroundColor: coral[6],
          color: 'white',
          boxShadow: isOpen ? 'none' : shadows.card,
          opacity: isOpen ? 0 : 1,
          transform: isOpen ? 'scale(0)' : 'scale(1)',
          transition: `opacity ${motion.duration.fast} ${motion.easing.standard}, transform ${motion.duration.fast} ${motion.easing.standard}, box-shadow ${motion.duration.fast} ${motion.easing.standard}`,
          pointerEvents: isOpen ? 'none' : 'auto'
        }}
        styles={{
          root: {
            '&:hover': {
              backgroundColor: coral[7]
            }
          }
        }}
        aria-label="Open action menu"
      >
        <IconPlus size={28} />
      </ActionIcon>

      {/* Action buttons -- appear when menu is open */}
      <Transition mounted={isOpen} transition="scale-y" duration={200} timingFunction="ease">
        {(transitionStyles) => (
          <div style={{ ...transitionStyles, transitionDelay: isOpen ? '50ms' : '0ms' }}>
            <KayakSearchButton goOpenKayakDialog={handleAction(onOpenKayakDialog)} />
          </div>
        )}
      </Transition>

      <Transition mounted={isOpen} transition="scale-y" duration={200} timingFunction="ease">
        {(transitionStyles) => (
          <div style={{ ...transitionStyles, transitionDelay: isOpen ? '120ms' : '0ms' }}>
            <AskAIButton onOpenDialog={handleAction(onOpenAIDialog)} />
          </div>
        )}
      </Transition>
    </Box>
  );
};

FloatingActionMenu.propTypes = {
  onOpenAIDialog: PropTypes.func.isRequired,
  onOpenKayakDialog: PropTypes.func.isRequired
};

export default FloatingActionMenu;
